import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectOpenDropdownId } from '@/store/ui/uiSelectors'
import { setOpenDropdown } from '@/store/ui/uiSlice'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import Dropdown from '@/components/ui/Dropdown/Dropdown'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem'
import IconCheck from '@/assets/icons/icon-check.svg?react'
import IconChevron from '@/assets/icons/icon-arrow-down.svg?react'
import { GROUPING_MODES, GROUPING_MODE_TITLES, type GroupingMode } from '@/utils/taskGrouping'
import css from './GroupingSelect.module.css'

const DROPDOWN_ID = 'project-grouping'

interface GroupingSelectProps {
    mode: GroupingMode
    onChange: (mode: GroupingMode) => void
}

// Always rendered, including on the default 'Sections' mode: the trigger doubles as the only
// on-screen sign that the list has been regrouped. Without it a user who switched to Assignee
// sees "Karl / Anna / Unassigned" where their sections used to be and concludes the sections
// are gone.
const GroupingSelect = ({ mode, onChange }: GroupingSelectProps) => {
    const dispatch = useAppDispatch()
    const isOpen = useAppSelector(selectOpenDropdownId) === DROPDOWN_ID

    const handleTriggerClick = () => {
        dispatch(setOpenDropdown(isOpen ? null : DROPDOWN_ID))
    }

    const handleSelect = (next: GroupingMode) => {
        dispatch(setOpenDropdown(null))
        if (next !== mode) onChange(next)
    }

    return (
        <DropdownWrapper>
            <button
                className={css.trigger}
                onClick={handleTriggerClick}
                data-testid="grouping-select-btn"
            >
                {GROUPING_MODE_TITLES[mode]}
                <IconChevron className={css.chevron} />
            </button>
            <Dropdown
                isOpen={isOpen}
                onClose={() => dispatch(setOpenDropdown(null))}
                width="small"
            >
                {GROUPING_MODES.map(option => (
                    <DropdownItem
                        key={option}
                        title={GROUPING_MODE_TITLES[option]}
                        rightIcon={option === mode ? <IconCheck /> : undefined}
                        onClick={() => handleSelect(option)}
                        testId="grouping-option"
                    />
                ))}
            </Dropdown>
        </DropdownWrapper>
    )
}

export default GroupingSelect
